import React, { useState, useEffect } from "react";
import { FiCalendar, FiCheckSquare, FiGrid, FiUsers } from "react-icons/fi";

const ProcessStep = ({ number, title, description, icon, isLast }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative flex mb-8"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative">
        {/* Icon circle */}
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center z-10 relative transition-all duration-300 ${
            isHovered 
              ? "bg-accent shadow-xl shadow-accent/40 ring-2 ring-accent/20 ring-offset-1 ring-offset-dark" 
              : "bg-accent shadow-md shadow-accent/20"
          }`}
        >
          {icon}
        </div>

        {/* Connector line - only show if not the last step */}
        {!isLast && (
          <div
            className={`absolute left-1/2 top-[56px] w-0.5 h-[calc(100%+32px)] -translate-x-1/2 transition-all duration-300 ${
              isHovered 
                ? "bg-accent/70 blur-[0.5px] shadow-[0_0_8px_1px_rgba(74,144,226,0.5)]" 
                : "bg-accent/40"
            }`}
          ></div>
        )}
      </div>

      {/* Content area */}
      <div
        className={`ml-6 pb-6 rounded-lg transition-all duration-300 ${
          isHovered 
            ? "bg-accent/5 transform scale-[1.005] p-3 shadow-lg shadow-accent/10" 
            : "p-3"
        }`}
      >
        <h3 className="text-light text-xl md:text-2xl font-medium">
          <span
            className={`transition-all duration-300 ${
              isHovered 
                ? "text-accent drop-shadow-[0_0_3px_rgba(74,144,226,0.4)] font-bold" 
                : "text-accent"
            }`}
          >
            Step {number}:
          </span>{" "}
          <span className={`transition-all duration-300 ${isHovered ? "text-light/100" : "text-light/90"}`}>
            {title}
          </span>
        </h3>
        <div className={`mt-3 transition-all duration-300 ${isHovered ? "text-light/90" : "text-light/70"}`}>{description}</div>
      </div>
    </div>
  );
};

const ProcessFlow = () => {
  useEffect(() => {
    // Load the Calendly script
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    // Load the Calendly CSS
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Clean up on unmount
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Function to open Calendly popup
  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/brad-wilde/eluno-consulting-introductory-call",
      });
    }
  };

  const steps = [
    {
      number: 1,
      title: "Book a Free Consultation",
      icon: <FiCalendar size={24} className="text-light" />,
      description: (
        <div>
          <p className="mb-4">
            Schedule a complimentary 30-minute call directly through our
            integrated scheduling system. We'll discuss your goals, identify
            challenges, and explore potential strategies.
          </p>
          <div className="p-6 bg-dark/50 border border-light/10 rounded-lg mb-4 text-center">
            <button
              onClick={openCalendly}
              className="bg-accent hover:bg-accent/90 text-dark py-3 px-6 rounded-lg transition text-lg font-medium"
            >
              Schedule Your Free Call
            </button>
          </div>
          <a href="/contact" className="text-accent hover:underline">
            If the scheduling system isn't loading, click here to book directly
          </a>
        </div>
      ),
    },
    {
      number: 2,
      title: "Select Your Engagement Model",
      icon: <FiCheckSquare size={24} className="text-light" />,
      description: (
        <div>
          <p className="mb-3">
            Choose the consulting option that best fits your needs:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-accent mr-2">•</span>
              <span>
                <strong>Focused Strategy Session:</strong> $75/session – ideal
                for immediate, actionable insights.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-accent mr-2">•</span>
              <span>
                <strong>Standard Engagement:</strong> From $125/hr – flexible
                advisory for evolving needs.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-accent mr-2">•</span>
              <span>
                <strong>Fixed-Fee Project:</strong> Tailored pricing based on
                scope and milestones.
              </span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      number: 3,
      title: "Collaborative Execution",
      icon: <FiGrid size={24} className="text-light" />,
      description: (
        <p>
          Experience structured, transparent consulting delivery with clear
          milestones, regular communication, and measurable outcomes.
        </p>
      ),
    },
    {
      number: 4,
      title: "Continued Partnership",
      icon: <FiUsers size={24} className="text-light" />,
      description: (
        <p>
          Maintain momentum with optional retainer-based services or easily
          extend our partnership for additional strategic projects and support.
        </p>
      ),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto py-12">
      <div>
        {steps.map((step, index) => (
          <ProcessStep
            key={step.number}
            number={step.number}
            title={step.title}
            description={step.description}
            icon={step.icon}
            isLast={index === steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default ProcessFlow;
